import React, { useState, useEffect } from "react";
import { Button, Row, Col } from "react-bootstrap";
import { FaGripVertical } from "react-icons/fa";
import { PiTrashLight } from "react-icons/pi";
import { MdOutlineModeEditOutline } from "react-icons/md";
import CustomButton from "./CustomButton";
import AddTaskField from "./modals/AddTaskField";
import { getCustomFields, deleteCustomField } from "./../api/Api";
import BreadCrumb from "./BreadCrumb";
import { useParams } from "react-router-dom";
import { showToast } from "../utils/toastUtils";
import styles from "./FieldSettings.module.css"; // Import the CSS module
import DeleteModel from "./modals/DeleteModel";

const FieldSettings = () => {
  const [fields, setFields] = useState([]);
  const [currentField, setCurrentField] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const { projectId } = useParams();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);

  // Predefined existing fields
  const existingFields = [
    { id: 1, name: "Title", type: "Text" },
    { id: 2, name: "Due Date", type: "Date" },
    { id: 3, name: "Hours", type: "Date" },
    { id: 4, name: "Priority", type: "Date" },
    { id: 5, name: "Assignee", type: "List" },
    { id: 6, name: "Workstream", type: "Date" },
  ];

  // Function to fetch custom fields from the backend
  const fetchFields = async () => {
    setLoading(true);
    try {
      const data = await getCustomFields(projectId);
      setFields(data?.data?.custom_fields || []);
    } catch (error) {
      showToast("Error fetching fields.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFields();
  }, [projectId]);

  const refresh = () => {
    fetchFields();
  };

  const handleShow = (field = null) => {
    setCurrentField(field);
    setShowModal(true);
  };

  const handleClose = () => {
    setCurrentField(null);
    setShowModal(false);
  };

  const handleDeleteClick = (field_id) => {
    setFieldToDelete(field_id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteCustomField(projectId, fieldToDelete);
      refresh();
      showToast("Field deleted successfully.", "success");
    } catch (error) {
      showToast("Error deleting field.", "error");
    } finally {
      setShowDeleteModal(false);
      setFieldToDelete(null);
    }
  };

  const breadcrumbItems = [
    { title: "Projects", link: `/project/${projectId}` },
    { title: "Goals", link: `/project/${projectId}?tab=goals` }, //with query parameter.
    { title: "Settings", link: "/projects/task-details" },
  ];

  return (
    <div className={`${styles.container} fs-4 m-3 pb-3`}>
      <BreadCrumb
        array={breadcrumbItems}
        style={{ backgroundColor: "#dddddd" }}
        button={
          <div className={styles.textEnd}>
            <CustomButton className="mb-3 mt-3" onClick={() => handleShow()}>
              + Add Field
            </CustomButton>
          </div>
        }
      />

      <div className={`${styles.mb5} ${styles.px3}`}>
        <div className="mb-5 mt-4">
          <p style={{ color: "gray", fontSize: "18px", fontWeight: "bold" }}>
            Manage the fields used in your forms, Add, edit or remove attributes
            as needed.
          </p>
        </div>

        <Row className="mb-3 g-0 p-0 p3">
          <Col xs={3} sm={5} md={2} className="p-0">
            <strong className={styles.fieldName}>Field Name</strong>
          </Col>
          <Col xs={6} sm={2} md={3} className="p-0 ">
            <strong className="mx-2">Field Type</strong>
          </Col>
          <Col xs={2} sm={5} md={2} className="p-0">
            <strong className={styles.actions}>Actions</strong>
          </Col>
        </Row>

        {/* Map through existing fields */}
        {existingFields.map((field) => (
          <Row key={field.id} className="mb-3 g-0 p-0 p4">
            <div
              className="d-flex align-items-center"
              style={{ width: "40px" }}
            >
              <FaGripVertical style={{ color: "#e2dfde" }} />
            </div>
            <Col xs={3} sm={3} md={2} className="p-0">
              <span
                style={{ color: "gray" }}
                className={`${styles.customTruncate}`}
              >
                {field.name}
              </span>
            </Col>
            <Col xs={4} sm={3} md={2} className="p-0">
              <span style={{ color: "gray" }}>{field.type}</span>
            </Col>
            <Col xs={3} sm={3} md={2} className="p-0 text-center">
              <Button variant="link" size="sm" disabled>
                <MdOutlineModeEditOutline size={18} color="#e5e593" />
              </Button>
              <Button variant="link" size="sm" disabled>
                <PiTrashLight size={18} color="#f08556" />
              </Button>
            </Col>
          </Row>
        ))}

        {/* Map through fetched custom fields */}
        {!loading ? (
          fields.length > 0 ? (
            fields.map((field) => (
              <Row key={field.id} noGutters className="mb-3 g-0 p-0 p4">
                <div
                  className="d-flex align-items-center"
                  style={{ width: "40px" }}
                >
                  <FaGripVertical style={{ color: "#8d8d8c" }} />
                </div>
                <Col xs={3} sm={3} md={2} className="py-1">
                  <span className={`${styles.customTruncate}`}>
                    {field.name}
                  </span>
                </Col>
                <Col xs={4} sm={3} md={2} className="py-1">
                  <span>{field.type}</span>
                </Col>
                <Col xs={3} sm={3} md={2} className="p-0 text-center">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => handleShow(field)}
                  >
                    <MdOutlineModeEditOutline size={18} color="#e5e593" />
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => handleDeleteClick(field.id)}
                  >
                    <PiTrashLight size={18} color="red" />
                  </Button>
                </Col>
              </Row>
            ))
          ) : (
            <div className="d-flex text-center px-5 p4 ">
              No custom fields added yet.
            </div>
          )
        ) : (
          <div>Loading...</div>
        )}

        {/* Add/Edit Task Field Modal */}
        {showModal && (
          <AddTaskField
            show={showModal}
            handleClose={handleClose}
            currentField={currentField}
            refresh={refresh}
            projectId={projectId}
          />
        )}

        {/* Delete Confirmation Modal */}
        <DeleteModel
          open={showDeleteModal}
          closeAlert={() => setShowDeleteModal(false)}
          onClick={handleDelete}
          type="field"
        />
      </div>
    </div>
  );
};

export default FieldSettings;
